import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import ImageWithFallback from '@/components/ImageWithFallback';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { seoData } from '@/utils/seoData';

const BlogPage = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    tr: {
      title: 'Blog',
      subtitle: 'Endüstriyel otomasyon dünyasından haberler ve makaleler',
      loading: 'Yüklen iyor...',
      readMore: 'Devamını Oku',
    },
    en: {
      title: 'Blog',
      subtitle: 'News and articles from the world of industrial automation',
      loading: 'Loading...',
      readMore: 'Read More',
    },
  };

  const t = content[language];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog?published_only=true`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const pageSeo = seoData.blog[language];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="blog-title"
          >
            {t.title}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600" data-testid="blog-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center text-gray-600">{t.loading}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="relative overflow-hidden h-48">
                    <ImageWithFallback
                      src={post.image_url} 
                      alt={language === 'tr' ? post.title_tr : post.title_en}
                      fallbackType="blog"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {language === 'tr' ? post.title_tr : post.title_en}
                    </h3>
                    <p className="text-gray-700 mb-4 line-clamp-3">
                      {language === 'tr' ? post.excerpt_tr : post.excerpt_en}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {format(new Date(post.created_at), 'dd MMM yyyy', {
                            locale: language === 'tr' ? tr : enUS
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center text-blue-600 font-medium">
                      {t.readMore}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;